/** Campaign timeline strip — one lane per pack with progress + event chips. */
import type { CampaignTimelineEntry, CalendarEvent } from "@/entities/mission-control";
import { EventChip } from "./EventChip";

interface Props {
  timeline: CampaignTimelineEntry[];
  onOpen: (e: CalendarEvent) => void;
}

const HEALTH_TONE: Record<"green" | "yellow" | "red", string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-rose-500",
};

export function CampaignTimeline({ timeline, onOpen }: Props): JSX.Element {
  if (timeline.length === 0) {
    return (
      <div
        data-testid="mc-timeline-strip"
        className="rounded-lg border border-dashed border-border/60 bg-muted/20 px-4 py-6 text-center text-xs text-muted-foreground"
      >
        No campaign packs yet. Create one in AI Studio to see it land here.
      </div>
    );
  }
  return (
    <div data-testid="mc-timeline-strip" className="space-y-3">
      {timeline.map((row) => (
        <div
          key={row.pack_id}
          data-testid={`mc-pack-lane-${row.pack_id}`}
          className="rounded-lg border border-border/60 bg-card p-3 shadow-sm"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${HEALTH_TONE[row.progress.health]}`} />
                <span className="text-sm font-semibold">
                  {row.campaign_name ?? row.objective ?? "Campaign"}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {row.preset ?? "custom"} · {row.total_assets} assets
                </span>
              </div>
              <div className="mt-1 h-1.5 w-64 overflow-hidden rounded-full bg-muted">
                <div
                  data-testid={`mc-pack-progress-${row.pack_id}`}
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${row.progress.overall_pct}%` }}
                />
              </div>
              <div className="mt-1 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {row.progress.stages.map((s) => (
                  <span key={s.key}>
                    {s.key}: {s.done}/{s.total}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              overall {row.progress.overall_pct}%
            </div>
          </div>
          <div className="mt-3 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1">
            {row.events.length === 0 ? (
              <div className="rounded-md border border-dashed border-border/40 px-3 py-2 text-[11px] text-muted-foreground">
                No calendar entries yet.
              </div>
            ) : (
              row.events.map((e) => (
                <div key={e.id} className="min-w-[180px] snap-start">
                  <EventChip event={e} onOpen={onOpen} />
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
